/**
 * WhitepaperDownloadsTile — super-admin dashboard tile that shows
 * which whitepapers (PDFs) prospects actually grabbed in the last
 * window. Pairs with `routes/whitepaper_analytics.py`.
 *
 * Two columns per row:
 *   - whitepaper slug + total clicks (right-aligned, big number)
 *   - sub-line: source breakdown ("header 4 · CTA 2") so you can
 *     see which placement converts better
 *
 * Empty-state message instead of an empty list — mirrors the
 * SalesIntelTile / TourFunnelTile empty-state pattern.
 */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { FileDown } from 'lucide-react';
import { useTelemetryTile } from '../../hooks';


const WINDOW_OPTIONS = [
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
];


// Friendly labels — slug → display. New whitepapers fall back to the
// slug verbatim, so no break on rollout of a new asset.
const LABELS = {
  maturity_governance: 'Learning Governance Whitepaper',
  lei_measures: 'LEI Measures Guide',
};


export default function WhitepaperDownloadsTile() {
  const [windowDays, setWindowDays] = useState(30);
  const { data, loading, error } = useTelemetryTile({
    url: `/analytics/whitepaper/usage?window_days=${windowDays}`,
    deps: [windowDays],
  });

  if (error === 'forbidden') return null;

  const usage = data?.usage || [];
  const totalEvents = data?.totals?.events || 0;

  return (
    <Card
      data-testid="whitepaper-downloads-tile"
      className="border-violet-200 bg-gradient-to-br from-white to-violet-50/40"
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <FileDown className="h-4 w-4 text-violet-500" />
              Whitepaper Downloads
            </CardTitle>
            <CardDescription className="mt-0.5">
              Which PDFs prospects actually clicked. Sources show whether the header pill or the CTA card converts better.
            </CardDescription>
          </div>
          <div
            className="inline-flex bg-white border border-slate-200 rounded-md p-0.5"
            data-testid="whitepaper-window-selector"
          >
            {WINDOW_OPTIONS.map((o) => (
              <button
                key={o.days}
                type="button"
                onClick={() => setWindowDays(o.days)}
                className={
                  'px-2 py-0.5 text-[11px] font-medium rounded transition-colors ' +
                  (windowDays === o.days
                    ? 'bg-violet-500 text-white'
                    : 'text-slate-600 hover:bg-slate-100')
                }
                data-testid={`whitepaper-window-${o.days}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-xs text-slate-500" data-testid="whitepaper-loading">Loading…</p>
        ) : error ? (
          <p className="text-xs text-rose-600" data-testid="whitepaper-error">
            Couldn't load whitepaper telemetry.
          </p>
        ) : totalEvents === 0 ? (
          <p
            className="text-xs text-slate-500"
            data-testid="whitepaper-empty"
          >
            No PDF downloads in the last {windowDays} days yet. The list populates the first time someone clicks a whitepaper link.
          </p>
        ) : (
          <ol className="space-y-2" data-testid="whitepaper-list">
            {usage.map((u, i) => (
              <li
                key={u.whitepaper}
                className="flex items-start justify-between gap-3 px-3 py-2 rounded-md border border-slate-200 bg-white"
                data-testid={`whitepaper-row-${i}`}
              >
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold text-slate-800 truncate">
                    {LABELS[u.whitepaper] || u.whitepaper}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    {u.unique_users} unique{' · '}
                    {Object.entries(u.sources)
                      .filter(([, c]) => c > 0)
                      .map(([s, c]) => `${s.replace('_', ' ')} ${c}`)
                      .join(' · ') || 'no source'}
                  </p>
                </div>
                <span className="text-lg font-bold text-violet-700 tabular-nums shrink-0">
                  {u.count}
                </span>
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}
